import { Form, Formik } from 'formik';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLoaderData } from 'react-router-dom';

import { useLogin } from '@/v5/@hooks/auth/login/useLogin';
import { useAuthFormContext } from '@/v5/contexts/AuthFormContext/useAuthFormContext';
import { clearRegisterSuccess } from '@/store/actions/register';

import { LoginMfa, MfaWizard } from 'pages/auth/new';
import { LoginNormal } from './LoginNormal';

import { MFA_LOGIN, MFA_SETUP, NORMAL_LOGIN } from '@/v5/@constants/login';
import { loginValidationSchema } from '@v5/validation/authValidation';

function LoginForm({ loginAction }) {
    const dispatch = useDispatch();
    const { formState, updateField } = useAuthFormContext();
    const isSubmittingRef = useRef(false);
    const initialValues = {
        email: '',
        password: '',
    };
    const message = useLoaderData();

    const {
        authResponse,
        cancelLogin,
        isChecked,
        setShowError,
        loginError,
        handleSubmit,
    } = useLogin();

    // NOTE: reset login action and clear register success on mount
    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            dispatch({ type: 'CLEAR_LOGIN_ACTION' });
            dispatch(clearRegisterSuccess());
        }
        return () => (isMounted = false);
    }, [dispatch]);

    const handleFormSubmit = async (values, formikHelpers) => {
        // Prevent multiple submissions synchronously
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;
        
        try {
            // NOTE: only trigger form submit on normal login, MFA_LOGIN has its own submit handler
            if (loginAction === NORMAL_LOGIN) {
                await handleSubmit(values, formikHelpers);
            }
        } finally {
            // Reset after a delay to allow for error handling
            setTimeout(() => {
                isSubmittingRef.current = false;
            }, 1000);
        }
    };

    return (
        <>
            <Formik
                initialValues={{
                    email: formState.email || initialValues.email,
                    password: formState.password || initialValues.password,
                }}
                enableReinitialize
                validationSchema={loginValidationSchema}
                onSubmit={handleFormSubmit}>
                {({
                    values,
                    setValues,
                    isSubmitting,
                    setSubmitting,
                    setTouched,
                    status,
                }) => (
                    <Form>
                        {loginAction === NORMAL_LOGIN && (
                            <LoginNormal
                                isChecked={isChecked}
                                isSubmitting={isSubmitting}
                                loginError={loginError}
                                message={message}
                                status={status}
                                setShowError={setShowError}
                                updateField={updateField}
                                values={values}
                                setValues={setValues}
                            />
                        )}

                        {loginAction === MFA_LOGIN && (
                            <LoginMfa
                                username={values.email}
                                password={values.password}
                                cancelLogin={cancelLogin}
                                setSubmitting={setSubmitting}
                                setValues={setValues}
                                setTouched={setTouched}
                            />
                        )}
                        {loginAction === MFA_SETUP && (
                            <MfaWizard
                                authResponse={authResponse}
                                username={values.email}
                                password={values.password}
                            />
                        )}
                    </Form>
                )}
            </Formik>
        </>
    );
}

export { LoginForm };
